import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from 'store';
import { setCameraPermissionState, setMicrophonePermissionState, userActions } from 'user/state';

import { DeviceKind, DeviceOutcome, acquireDevices } from '../core';
import { getMediaDevices } from '../utils';

type LegacyPermission = 'granted' | 'denied' | 'system_denied' | 'prompt';

/**
 * Mirror a rich {@link DeviceOutcome} onto the legacy permission enum so existing
 * consumers keep working unchanged: Agora track gating (`=== 'granted'`), the
 * PermissionDeniedModal, and `selectHasMicrophoneAccess`.
 *
 * The new-only failure outcomes (no_device / device_unavailable / dismissed /
 * unsupported) have no legacy equivalent. We keep legacy at `'prompt'` so track
 * gating correctly creates no track — but the reactive overlay no longer keys off
 * `'prompt'`, so this no longer leaves "Click Allow" stuck on screen.
 */
const outcomeToLegacyPermission = (outcome: DeviceOutcome): LegacyPermission => {
  switch (outcome) {
    case DeviceOutcome.Granted:
      return 'granted';
    case DeviceOutcome.PermissionDenied:
      return 'denied';
    case DeviceOutcome.SystemDenied:
      return 'system_denied';
    default:
      return 'prompt';
  }
};

type ThunkApi = { state: RootState; dispatch: AppDispatch };

/**
 * The single entry point for acquiring devices. Requests the kinds together in one
 * `getUserMedia` (a single merged prompt), falling back to per-kind acquisition only on a
 * missing-device failure — see {@link acquireDevices}. Mirrors the legacy permission state
 * for back-compat, and refreshes the device list so labels become visible once permission
 * is granted.
 *
 * The returned outcomes become the thunk's `fulfilled` payload; the `devices` slice
 * stores them and manages `isAcquiring` off the thunk lifecycle (pending/fulfilled/
 * rejected), so the in-flight flag is always cleared even if this body throws.
 *
 * Must be dispatched from a user gesture for Safari (the facade calls it from the
 * relevant click handler).
 */
export const acquireDevicesThunk = createAsyncThunk<
  Partial<Record<DeviceKind, DeviceOutcome>>,
  Partial<Record<DeviceKind, boolean>>,
  ThunkApi
>('[devices] acquire', async (request, { dispatch }) => {
  const outcomes = await acquireDevices(request);

  if (outcomes.microphone !== undefined) {
    dispatch(setMicrophonePermissionState(outcomeToLegacyPermission(outcomes.microphone)));
  }
  if (outcomes.camera !== undefined) {
    dispatch(setCameraPermissionState(outcomeToLegacyPermission(outcomes.camera)));
  }

  const devices = await getMediaDevices();
  dispatch(userActions.navigator.mediaDevices.devicesFound(devices));

  return outcomes;
});
